import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import CustomSelect from "~components/CustomSelect";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { CompetitorTypes } from "~enums/competitorTypes";
import { FixtureStatuses } from "~enums/fixtureStatuses";
import { PermissionNames } from "~enums/permissions.ts";
import { useFetchSeason } from "~hooks/apiHooks/useFetchSeason.ts";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useNotification } from "~hooks/useNotification.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import AddPlayerToRosterModal from "~pages/Roster/components/AddPlayerToRoster/AddPlayerToRosterModal.tsx";
import { RosterTableColumns } from "~pages/Roster/enums/rosterTableColumns.ts";
import AddPlayerToFixtureRoster from "~pages/Roster/FixtureRosterListPage/components/AddPlayerToFixtureRoster.tsx";
import {
  defaultFixtureRosterExportFieldNames,
  fixtureRosterExportFields,
} from "~pages/Roster/FixtureRosterListPage/constants/exportFields.ts";
import styles from "~pages/Roster/FixtureRosterListPage/FixtureRosterListPage.module.css";
import useFetchDefaultFixtureProfile from "~pages/Roster/FixtureRosterListPage/hooks/useFetchDefaultFixtureProfile";
import { useGetFixture } from "~pages/Roster/FixtureRosterListPage/hooks/useFetchFixture";
import useFetchFixtureRoster from "~pages/Roster/FixtureRosterListPage/hooks/useFetchFixtureRoster";
import { useFixtureRosterService } from "~pages/Roster/FixtureRosterListPage/hooks/useFixtureRosterService.ts";
import { useGetFixtureRosterDataTableColumns } from "~pages/Roster/FixtureRosterListPage/hooks/useGetFixtureRosterDataTableColumns.tsx";
import { useGetSeasonFixtureProfile } from "~pages/Roster/FixtureRosterListPage/hooks/useGetSeasonFixtureProfile";
import type { FixtureRosterDataTableRow } from "~pages/Roster/FixtureRosterListPage/types/fixtureRosterDataTableRow.ts";
import type { UpsertFixtureRosterModel } from "~pages/Roster/FixtureRosterListPage/types/upsertFixtureRosterModel";
import useFetchEntity from "~pages/Roster/hooks/useFetchEntity.ts";
import { useFetchInternationalGamesAmount } from "~pages/Roster/hooks/useFetchInternationalGamesAmount.ts";
import { useRosterCaptainToggle } from "~pages/Roster/hooks/useRosterCaptainToggle.ts";
import type { SeasonResponse } from "~types/season.ts";

const FixtureRosterListPage = () => {
  const { fixtureId, seasonId, sport, organizationId, customerId } = useParams();
  const { competitorType, uiConfiguration } = useSportConfiguration();
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  if (competitorType === CompetitorTypes.Person) {
    throw Error("Fixture roster does not support for individual sports");
  }

  const { formatMessage } = useIntl();
  const notification = useNotification();
  const queryClient = useQueryClient();

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);

  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const params: Record<string, string> = {
    include: ["entities", "seasons", "persons", "fixture"].join(","),
    limit: "20",
    offset: "0",
    nameFullLocalContains: searchText,
  };

  const [selectedEntrant, setSelectedEntrant] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [entrantSelectorOptions, setEntrantSelectorOptions] = useState<{ value: string; label: string }[]>([]);

  const { data: roster, isLoading: isRosterLoading } = useFetchFixtureRoster({
    customParams: params,
    entityId: selectedEntrant?.value,
    fixtureId,
  });
  const { data: fixture, isLoading: isFixtureLoading } = useGetFixture();
  const { seasonFixtureProfile, isSeasonLoading } = useGetSeasonFixtureProfile();
  const { data: defaultFixtureProfile, isLoading: isDefaultProfileLoading } = useFetchDefaultFixtureProfile();

  const isRosterEditable =
    !!fixture &&
    ![FixtureStatuses.Bye, FixtureStatuses.Confirmed, FixtureStatuses.Abandoned, FixtureStatuses.Cancelled].includes(fixture.status);

  const { data: entity } = useFetchEntity(selectedEntrant?.value);

  const { data: season } = useFetchSeason(seasonId, {}, (seasonData: SeasonResponse) => {
    return seasonData.data[0] || {};
  });

  const { data: intGamesAmount, isLoading: isIntGamesAmountPending } = useFetchInternationalGamesAmount({
    representing: entity?.representing,
    seasonId: seasonId,
    discipline: season?.discipline,
  });

  // Memoized so row objects keep a stable identity across unrelated re-renders,
  // which row.disabled mutation (set while a field is saving) relies on.
  const rosterTableData = useMemo(
    () =>
      roster?.map((item) => ({
        ...item,
        caps: intGamesAmount ? intGamesAmount[item.personId] : "",
      })),
    [roster, intGamesAmount],
  );

  const maxRosterSize =
    fixture?.fixtureProfile?.profile.maximumRosterSize ||
    seasonFixtureProfile?.profile.maximumRosterSize ||
    defaultFixtureProfile?.profile.maximumRosterSize;
  const maxStarterSize =
    fixture?.fixtureProfile?.profile.maximumPlayersOnCourt ||
    seasonFixtureProfile?.profile.maximumPlayersOnCourt ||
    defaultFixtureProfile?.profile.maximumPlayersOnCourt;

  const starterSize = rosterTableData?.filter((item) => item.starter).length || 0;
  const rosterSize = rosterTableData?.length || 0;
  const isAddAllowed = maxRosterSize ? rosterSize < maxRosterSize : true;
  const isAddToStarterAllowed = maxStarterSize ? starterSize < maxStarterSize : true;

  const {
    upsertFixtureRosterPlayer,
    deleteFixtureRosterPlayer,
    isDeletePending: isRosterDeleting,
    exportFixtureRoster,
    isExportPending,
    importFixtureRoster,
  } = useFixtureRosterService(selectedEntrant?.value || "", fixtureId || "");

  const handleUpsert = async (value: UpsertFixtureRosterModel) => {
    if (fixtureId && selectedEntrant?.value) {
      await upsertFixtureRosterPlayer(
        {
          ...value,
          fixtureId,
          entityId: selectedEntrant?.value,
        },
        "fixture.roster.added.success",
      );
    } else {
      throw Error("Please specify fixtureId and entityId");
    }
  };

  const handleDelete = async (personId: string) => {
    await deleteFixtureRosterPlayer(personId);
  };

  const invalidateRoster = () =>
    queryClient.invalidateQueries({
      queryKey: ["fixtureRoster", sport, organizationId, seasonId, selectedEntrant?.value, fixtureId],
    });

  const { handleCaptainToggle, deleteCaptainRole } = useRosterCaptainToggle({
    entityId: selectedEntrant?.value,
    seasonId,
    fixtureId,
    onSuccess: invalidateRoster,
    successMessageId: "fixture.roster.updated.success",
    operationItem: ApiOperationItems.FIXTURE_ROSTER,
  });

  const handleUpdateField = async (
    row: FixtureRosterDataTableRow & DataTableRow,
    update: Partial<Pick<UpsertFixtureRosterModel, "bib" | "position" | "isCaptain" | "starter">>,
  ) => {
    if (!fixtureId || !selectedEntrant?.value) {
      return;
    }
    row.disabled = true;
    try {
      await upsertFixtureRosterPlayer(
        {
          personId: row.personId,
          entityId: selectedEntrant.value,
          fixtureId,
          ...update,
        },
        "fixture.roster.updated.success",
      );
    } finally {
      row.disabled = false;
    }
  };

  const addToRoster = async (value: any) => {
    const isPersonAlreadyInRoster = rosterTableData?.some((person) => person.personId === value.personId);
    if (isPersonAlreadyInRoster) {
      notification.error({
        message: formatMessage({ id: "roster.error.person.exist" }),
        placement: "bottomRight",
      });
      return;
    }
    await handleUpsert(value);
  };

  const columns = useGetFixtureRosterDataTableColumns({
    massEditMode,
    isRosterEditable,
    isAddToStarterAllowed,
    handleUpdateField,
    onCaptainToggle: handleCaptainToggle,
  });

  useEffect(() => {
    const entrantSelectorOptions = (fixture?.competitors || []).map((item) => ({
      label: item.name,
      value: item.id,
    }));

    setEntrantSelectorOptions(entrantSelectorOptions);
    setSelectedEntrant(entrantSelectorOptions[0] || null);
  }, [fixture]);

  const isLoading =
    isRosterLoading || isSeasonLoading || isDefaultProfileLoading || isFixtureLoading || isRosterDeleting || isIntGamesAmountPending;

  const title = fixture ? fixture.fixtureName : "";

  const getNavigateUrl = (personId: string) => `/${customerId}/admin/${sport}/${organizationId}/persons/manage/${personId}`;

  return (
    <Page title={`${title ?? ""} ${formatMessage({ id: "roster.fixture" })}`}>
      <AddPlayerToRosterModal open={isAddModalOpen} setOpen={setIsAddModalOpen}>
        <AddPlayerToFixtureRoster
          isAddAllowed={isAddAllowed}
          isLoading={isLoading}
          fixtureId={fixtureId}
          entityId={selectedEntrant?.value}
          handleAdd={addToRoster}
          tableData={rosterTableData || []}
        />
      </AddPlayerToRosterModal>
      {(maxRosterSize || maxStarterSize) && (
        <div style={{ display: "flex", gap: 10 }}>
          {!!maxStarterSize && <span>{`${formatMessage({ id: "roster.starter.size" })} ${starterSize}/${maxStarterSize}`}</span>}
          {!!maxRosterSize && <span>{`${formatMessage({ id: "roster.size" })} ${rosterSize}/${maxRosterSize}`}</span>}
        </div>
      )}
      <DataTable<FixtureRosterDataTableRow>
        headerCustomActions={
          <CustomSelect
            onChange={(_: unknown, value: unknown) => {
              setSelectedEntrant(value as { value: string; label: string });
            }}
            options={entrantSelectorOptions}
            value={selectedEntrant}
            placeholder={formatMessage({ id: "status" })}
            className={styles.select}
            disabled={isLoading}
          />
        }
        handleRefresh={invalidateRoster}
        columns={columns}
        rowActions={{
          base: {
            delete: {
              hidden: () => !isRosterEditable,
              onClick: async (row) => {
                if (row.isCaptain && row.captainRoleId) {
                  await deleteCaptainRole(row.captainRoleId);
                }
                await handleDelete(row.personId);
              },
              itemName: (row) => row.person.nameFullLocal || row.personId,
              loading: isRosterDeleting,
            },
            manage: {
              onClick: async (row) => {
                navigate(getNavigateUrl(row.personId));
              },
            },
          },
        }}
        rowKey="personId"
        rowNameKey="personId"
        dataSource={rosterTableData || []}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        isDataLoading={isLoading}
        handleAdd={isRosterEditable ? () => setIsAddModalOpen(true) : undefined}
        addButtonIntlKey="roster.add"
        onMassEditModeChangedHandler={isRosterEditable ? setMassEditMode : undefined}
        massEditMode={massEditMode}
        permissionNames={[PermissionNames.FIXTURE_ROSTER]}
        importConfig={{
          handleImport: importFixtureRoster,
          titleIntlKey: "import.modal.title.fixtureRoster",
          descriptionIntlKey: "import.modal.text.fixtureRoster",
        }}
        exportConfig={{
          handleExport: exportFixtureRoster,
          defaultFields: defaultFixtureRosterExportFieldNames,
          groupedFields: fixtureRosterExportFields,
          localStorageKey: "export.fixtureRoster",
          loading: isExportPending,
        }}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.fixture.roster`}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.fixture.roster`,
          defaultSorter: { columnKey: RosterTableColumns.Bib, order: "ascend" },
        }}
        dataTableUiConfiguration={uiConfiguration.pages.fixtureRoster.listPage}
        paginationConfig={{
          currentPage,
          pageSize,
          pageSizeOptions,
          handlePaginationChange,
          offset,
          total: rosterTableData?.length,
        }}
      />
    </Page>
  );
};

export default FixtureRosterListPage;
